import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../hooks/useAppDispatch'
import { useCreateDiscussionMutation } from '../store/api/discussionsApi'
import { useGetCategoriesQuery } from '../store/api/categoriesApi'
import { useLoginPrompt } from '../context/LoginPromptContext'
import { useToast } from '../context/ToastContext'
import { getErrorMessage, api } from '../lib/apiClient'
import { ENDPOINTS } from '../lib/endpoints'
import { COLOR_AI_BG, COLOR_AI_BORDER, MAX_DISCUSSION_TAGS, DISCUSSION_TITLE_MIN, DISCUSSION_TITLE_MAX, DISCUSSION_CONTENT_MIN, DISCUSSION_CONTENT_MAX } from '../lib/config'
import { normaliseTag } from '@ekkam/shared'
import PollBuilder, { type PollDraft } from '../components/poll/PollBuilder'
import { useCreatePollMutation } from '../store/api/pollsApi'
import { MultiSelectDropdown } from '../components/ui/MultiSelectDropdown'
import { Button, Card, TextInput, Textarea, Label, Alert, Badge } from '../components/ui'
import s from '../styles/pages/CreateDiscussionPage.module.css'

const DRAFT_KEY = 'ekkam_discussion_draft'

interface Draft { title: string; content: string; tags: string[]; savedAt: number }

function saveDraft(title: string, content: string, tags: string[]) {
  if (!title.trim() && !content.trim()) { localStorage.removeItem(DRAFT_KEY); return }
  const draft: Draft = { title, content, tags, savedAt: Date.now() }
  localStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
}

function loadDraft(): Draft | null {
  try { return JSON.parse(localStorage.getItem(DRAFT_KEY) ?? '') }
  catch { return null }
}

function clearDraft() { localStorage.removeItem(DRAFT_KEY) }

export default function CreateDiscussionPage() {
  const { isAuthenticated } = useAppSelector((s) => ({ isAuthenticated: s.auth.isAuthenticated }))
  const { user } = useAppSelector((s) => s.auth)
  const { trigger: promptLogin } = useLoginPrompt()
  const toast = useToast()
  const navigate = useNavigate()
  const { accessToken } = useAppSelector((s) => s.auth)
  const [createDiscussion, { isLoading }] = useCreateDiscussionMutation()
  const [createPoll] = useCreatePollMutation()
  const { data: categoriesData } = useGetCategoriesQuery()
  const categories = categoriesData?.categories ?? []
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [tagInput, setTagInput] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([])
  const [error, setError] = useState('')
  const [draft, setDraft] = useState<Draft | null>(null)
  const [draftRestored, setDraftRestored] = useState(false)
  const [poll, setPoll] = useState<PollDraft | null>(null)
  const [showMismatchConfirm, setShowMismatchConfirm] = useState(false)
  const autoSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // AI-powered mismatch detection — runs on submit, not on every keystroke
  const [mismatchedCategories, setMismatchedCategories] = useState<typeof categories>([])

  const [classifyLoading, setClassifyLoading] = useState(false)

  // Restore form if navigated back after an API failure
  useEffect(() => {
    const state = (window.history.state?.usr ?? {}) as any
    if (state.restoredTitle) {
      setTitle(state.restoredTitle)
      setContent(state.restoredContent ?? '')
      setTags(state.restoredTags ?? [])
      setDraftRestored(true)
      window.history.replaceState({}, '')
      return
    }
    const saved = loadDraft()
    if (saved && (saved.title.trim() || saved.content.trim())) setDraft(saved)
  }, [])

  // Auto-save draft 1s after user stops typing
  useEffect(() => {
    if (!draftRestored && !title && !content) return
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current)
    autoSaveRef.current = setTimeout(() => saveDraft(title, content, tags), 1000)
    return () => { if (autoSaveRef.current) clearTimeout(autoSaveRef.current) }
  }, [title, content, tags])

  const handleRestoreDraft = () => {
    if (!draft) return
    setTitle(draft.title)
    setContent(draft.content)
    setTags(draft.tags)
    setDraft(null)
    setDraftRestored(true)
  }

  const handleDiscardDraft = () => { clearDraft(); setDraft(null) }

  const hasPromptedRef = useRef(false)
  useEffect(() => {
    if (!isAuthenticated && !hasPromptedRef.current) {
      hasPromptedRef.current = true
      promptLogin('Sign in to start a new discussion')
    }
  }, [])

  const addTag = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      const tag = normaliseTag(tagInput)
      if (tag && !tags.includes(tag) && tags.length < MAX_DISCUSSION_TAGS) {
        setTags([...tags, tag])
        setTagInput('')
      }
    }
  }

  const removeTag = (tag: string) => setTags(tags.filter((t) => t !== tag))

  const handleSubmit = async (force = false) => {
    if (!isAuthenticated) {
      promptLogin('Sign in to post your discussion')
      return
    }
    setError('')
    if (!title.trim()) { setError('Title is required'); return }
    if (title.length < DISCUSSION_TITLE_MIN) { setError(`Title must be at least ${DISCUSSION_TITLE_MIN} characters`); return }
    if (!content.trim()) { setError('Content is required'); return }
    if (content.length < DISCUSSION_CONTENT_MIN) { setError(`Content must be at least ${DISCUSSION_CONTENT_MIN} characters`); return }

    // Poll validation
    if (poll) {
      if (!poll.question.trim()) { setError('Poll question is required'); return }
      const filledOptions = poll.options.filter(o => o.trim())
      if (filledOptions.length < 2) { setError('Poll needs at least 2 options'); return }
    }

    // If categories selected, run AI mismatch check before posting
    if (!force && selectedCategoryIds.length > 0) {
      const text = `${title} ${content}`.trim()
      if (text.length >= 30) {
        setClassifyLoading(true)
        try {
          const result = await api.post<{ scores: { category: string; confidence: number }[] }>(
            'ai', ENDPOINTS.ai.classify, { text }, accessToken
          )
          const mismatched = selectedCategoryIds
            .map(id => categories.find(c => c.id === id))
            .filter((cat): cat is NonNullable<typeof cat> => !!cat)
            .filter(cat => {
              const score = result.scores.find(s => s.category === cat.slug)
              return !score || score.confidence < 0.4
            })
          if (mismatched.length > 0) {
            setMismatchedCategories(mismatched)
            setClassifyLoading(false)
            setShowMismatchConfirm(true)
            return
          }
        } catch {
          // fail silently — don't block posting if classify errors
        }
        setClassifyLoading(false)
      }
    }

    // Flush any uncommitted tag still sitting in the input box
    let finalTags = tags
    if (tagInput.trim()) {
      const tag = normaliseTag(tagInput)
      if (tag && !tags.includes(tag) && tags.length < MAX_DISCUSSION_TAGS) {
        finalTags = [...tags, tag]
        setTags(finalTags)
        setTagInput('')
      }
    }

    clearDraft()

    const tempId = `pending-${Date.now()}`
    void navigate(`/discussions/${tempId}`, {
      state: {
        optimistic: {
          title: title.trim(),
          content: content.trim(),
          tags: finalTags,
          author: { displayName: user?.displayName ?? user?.email ?? 'You', avatarUrl: user?.avatarUrl ?? null },
        },
      },
      replace: false,
    })

    try {
      const result = await createDiscussion({
        title: title.trim(),
        content: content.trim(),
        categoryIds: selectedCategoryIds,
        tags: finalTags,
      }).unwrap()

      if (poll) {
        const filledOptions = poll.options.filter(o => o.trim())
        void createPoll({
          discussionId: result.discussion.id,
          body: {
            question:    poll.question.trim(),
            options:     filledOptions,
            isAnonymous: poll.isAnonymous,
            allowRevote: poll.allowRevote,
            expiresAt:   poll.expiresAt,
          },
        }).catch(() => {})
      }

      void navigate(`/discussions/${result.discussion.id}`, {
        replace: true,
        state: {
          optimistic: {
            title: title.trim(),
            content: content.trim(),
            tags: finalTags,
            fresh: true,
            author: { displayName: user?.displayName ?? user?.email ?? 'You', avatarUrl: user?.avatarUrl ?? null },
          },
        },
      })
    } catch (err: any) {
      void navigate('/new', {
        replace: true,
        state: { restoredTitle: title.trim(), restoredContent: content.trim(), restoredTags: finalTags },
      })
      if (err?.status === 429 || err?.data?.retryAfterSeconds) {
        const mins = Math.ceil((err?.data?.retryAfterSeconds ?? 3600) / 60)
        toast.error(`Posting limit reached. Try again in ${mins} minute${mins !== 1 ? 's' : ''}.`)
      } else {
        toast.error(getErrorMessage(err, 'Failed to create discussion. Please try again.'))
      }
    }
  }

  const charCount = content.length

  return (
    <div className={s.page}>
      <div className={s.pageHeader}>
        <h1 className={s.pageTitle}>➕ Start a New Discussion</h1>
        <p className={s.pageSubtitle}>Ask anything — our AI agents will automatically detect the topic and respond</p>
      </div>

      {draft && (
        <div onClick={handleDiscardDraft} className={s.modalBackdrop}>
          <div onClick={(e) => e.stopPropagation()} className={s.draftModal}>
            <div className={s.draftIcon}>📝</div>
            <h3 className={s.draftTitle}>You have an unsaved draft</h3>
            <p className={s.draftName}><strong>"{draft.title.trim() || 'Untitled'}"</strong></p>
            <p className={s.draftQuestion}>Would you like to continue where you left off?</p>
            <div className={s.draftActions}>
              <button onClick={handleDiscardDraft} className={s.draftDiscardBtn}>Discard</button>
              <button onClick={handleRestoreDraft} className={s.draftRestoreBtn}>Restore draft</button>
            </div>
          </div>
        </div>
      )}

      {showMismatchConfirm && (
        <div onClick={() => setShowMismatchConfirm(false)} className={s.mismatchBackdrop}>
          <div onClick={(e) => e.stopPropagation()} className={s.mismatchModal}>
            <div className={s.mismatchHeader}>
              <div className={s.mismatchIcon}>⚠️</div>
              <div>
                <div className={s.mismatchTitle}>Wrong category selected</div>
                <div className={s.mismatchSub}>Doesn't match <span className={s.mismatchCatName}>{mismatchedCategories.map(c => c.name).join(', ')}</span></div>
              </div>
            </div>
            <div className={s.mismatchDivider} />
            <p className={s.mismatchBody}>The agent for this category may not give the best answer for your topic. Change the category for a better response, or post anyway.</p>
            <div className={s.mismatchActions}>
              <button onClick={() => setShowMismatchConfirm(false)} className={s.mismatchCancelBtn}>← Change category</button>
              <button onClick={() => { setShowMismatchConfirm(false); void handleSubmit(true) }} className={s.mismatchPostBtn}>Post anyway</button>
            </div>
          </div>
        </div>
      )}

      <Card style={{ width: '100%', boxSizing: 'border-box' }}>

        {error && <Alert variant="error" style={{ marginBottom: '1rem' }}>{error}</Alert>}

        {/* AI auto-detect notice */}
        <div className={s.aiNotice} style={{ background: COLOR_AI_BG, border: `1px solid ${COLOR_AI_BORDER}` }}>
          🤖 <span>
            {selectedCategoryIds.length > 0
              ? <><strong>Targeted mode</strong> — only the agent for your selected {selectedCategoryIds.length === 1 ? 'category' : 'categories'} will respond.</>
              : <><strong>AI agents auto-detect your topic</strong> — relevant experts will respond automatically. Select a category below to target a specific agent.</>}
          </span>
        </div>

        {/* Category picker */}
        <div className={s.categorySection}>
          <div className={s.categoryLabelRow}>
            <label className={s.categoryLabel}>
              Category <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional — leave blank for AI auto-detection)</span>
            </label>
            <MultiSelectDropdown
              options={categories.filter(c => c.slug !== 'general').map(c => ({
                value: c.id,
                label: c.name,
                icon: c.icon ?? undefined,
                color: c.color ?? undefined,
              }))}
              selected={selectedCategoryIds}
              onChange={setSelectedCategoryIds}
              placeholder="Select categories"
              searchPlaceholder="Search categories..."
            />
          </div>
          <div className={s.categoryHint}>Selecting a category directs your question to that specific AI agent</div>
          {selectedCategoryIds.length > 0 && (
            <div className={s.categoryChips}>
              {selectedCategoryIds.map(id => {
                const cat = categories.find(c => c.id === id)
                if (!cat) return null
                return (
                  <span key={id} className={s.categoryChip} style={{ background: `${cat.color ?? 'var(--accent-primary)'}18`, border: `1.5px solid ${cat.color ?? 'var(--accent-primary)'}55`, color: cat.color ?? 'var(--accent-primary)' }}>
                    {cat.icon && <span>{cat.icon}</span>}
                    {cat.name}
                    <button type="button" onClick={() => setSelectedCategoryIds(prev => prev.filter(i => i !== id))} className={s.categoryChipRemove}>✕</button>
                  </span>
                )
              })}
            </div>
          )}
        </div>

        <div className={s.fieldSection}>
          <Label style={{ display: 'block', marginBottom: '0.5rem' }}>Title *</Label>
          <TextInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What's your question or topic?" maxLength={DISCUSSION_TITLE_MAX} />
          <div className={s.charCount} style={{ textAlign: 'right' }}>{title.length}/{DISCUSSION_TITLE_MAX}</div>
        </div>

        <div className={s.fieldSection}>
          <Label style={{ display: 'block', marginBottom: '0.5rem' }}>Content *</Label>
          <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Describe your question in detail. The more context you provide, the better the AI response will be..." rows={8} maxLength={DISCUSSION_CONTENT_MAX} />
          <div className={s.charCount} style={{ textAlign: 'right', color: charCount < DISCUSSION_CONTENT_MIN ? 'var(--status-error)' : 'var(--text-muted)' }}>
            {charCount} characters {charCount < DISCUSSION_CONTENT_MIN && `(minimum ${DISCUSSION_CONTENT_MIN})`}
          </div>
        </div>

        <div className={s.tagsSection}>
          <label className={s.tagsLabel}>Tags <span className={s.tagsLabelOptional}>(optional, max {MAX_DISCUSSION_TAGS} — AI fills remaining)</span></label>
          <div className={s.tagsInput}>
            {tags.map((tag) => (
              <span key={tag} className={s.tagChip}>
                #{tag}
                <button onClick={() => removeTag(tag)} className={s.tagRemoveBtn}>×</button>
              </span>
            ))}
            {tags.length < MAX_DISCUSSION_TAGS && (
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={addTag}
                onBlur={() => { const tag = normaliseTag(tagInput); if (tag && !tags.includes(tag) && tags.length < MAX_DISCUSSION_TAGS) { setTags([...tags, tag]); setTagInput('') } }}
                placeholder={tags.length === 0 ? 'Add tags... (press Enter)' : ''}
                className={s.tagTextInput}
              />
            )}
          </div>
        </div>

        <div className={s.pollSection}><PollBuilder value={poll} onChange={setPoll} /></div>

        <div className={s.submitRow}>
          <Button variant="ghost" onClick={() => void navigate(-1)}>Cancel</Button>
          <Button onClick={() => void handleSubmit()} loading={isLoading || classifyLoading}>
            {classifyLoading ? '🔍 Checking...' : isLoading ? '⏳ Posting...' : '🚀 Post Discussion'}
          </Button>
        </div>
      </Card>
    </div>
  )
}
